import { useCallback, useEffect, useRef, useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { RUN_POLL_MS, getRun, isTerminal, type RunDetailOut } from "@/shared/api/solutionRuns";

/**
 * Load one solution run and keep it fresh while it is active: re-reads
 * `GET /solutions/runs/{id}` every `RUN_POLL_MS` while the status is `queued`
 * or `running`, and stops as soon as it is terminal (or on unmount). The
 * `useCollection` polling pattern.
 */
export function useSolutionRun(runId: string | null) {
  const getToken = useAccessToken();
  const [run, setRun] = useState<RunDetailOut | null>(null);
  const [loading, setLoading] = useState(Boolean(runId));
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    if (!runId) return;
    try {
      const token = await getToken();
      const next = await getRun(token, runId);
      if (alive.current) {
        setRun(next);
        setError(null);
      }
    } catch (err) {
      if (alive.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [getToken, runId]);

  useEffect(() => {
    alive.current = true;
    setRun(null);
    setLoading(Boolean(runId));
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload, runId]);

  const active = run !== null && !isTerminal(run.status);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => void reload(), RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [active, reload]);

  return { run, loading, error, reload };
}
